import React from 'react';
import uri from "urijs";

import Api from "./api.js";
import KeycloakApi from "./keycloak";
import {Kickoff} from "../src/kickoff/kickoff";
import {config} from "../src";

export namespace n8n {
	export interface Table {
		id: string,
		name: string,
		columns: Column[],
		projectId: string,
		createdAt: Date,
		updatedAt: Date
	}

	export interface Column {
		id: string,
		name: string,
		type: 'string' | 'number' | 'boolean' | 'date',
		index: number
	}

	export interface TableFilter {
		limit?: number,
		cursor?: string,
		filter?: string,
		sortBy?: string,
		search?: string
	}

	interface TableRow {
		id: number,
		createdAt: Date,
		updatedAt: Date
	}

	export type TableRowWith<T extends object> = T & TableRow;

	export type KickoffRunNr = number;
	export type KundenNr = number;
	export type ProjektNr = `${string}-${string}`;
	export type Variant = 'A' | 'B' | 'C';

	export interface KundenProjektAuftrag {
		kunde: n8n.KundenNr,
		projektnummer: n8n.ProjektNr,
		auftrag: string,
	}

	export interface Response<T> {
		data: T,
		nextCursor: string | null
	}

	export interface KickoffRun extends TableRow {
		id: n8n.KickoffRunNr,
		customerId: n8n.KundenNr,
		status: 'akquise' | 'in-bearbeitung' | 'rückfrage-ausstehend' | 'produktiv' | 'abgebrochen',
		job: string,
		project: ProjektNr,
	}
}

export default class N8nApi extends Api {
	constructor(base: URL, private readonly keycloak: KeycloakApi) {
		super(uri(base));
	}

	headers(): Record<string, string> {
		return Object.assign(super.headers(), {
			Authorization: `Bearer ${this.keycloak.access}`
		});
	}

	async kickoff(payload: Kickoff) {
		return await this.fetchJsonDirect(config.n8n["kickoff-webhook"], 'POST', {}, payload)
			.then(body => {
				if (Array.isArray(body) ? body.some(i => !i.success) : !body.success)
					throw body;
				return body;
			})
	}

	async getRecentRuns(num = 20): Promise<n8n.KickoffRun[]> {
		return await this.fetchJson<n8n.Response<n8n.TableRowWith<{
			kunde: n8n.KundenNr,
			auftrag: string,
			status: n8n.KickoffRun['status'],
			projektnummer: n8n.ProjektNr,
		}>[]>>(`/api/v1/data-tables/${config.n8n['customerTableId']}/rows?${new URLSearchParams({
			sortBy: 'createdAt:desc',
			limit: num.toFixed(0)
		}).toString()}`)
			.then(runs => runs.data.map(i => ({
				id: 0,
				customerId: i.kunde,
				job: i.auftrag,
				project: i.projektnummer,
				status: i.status,
				createdAt: new Date(i.createdAt),
				updatedAt: new Date(i.updatedAt)
			} satisfies n8n.KickoffRun)))
	}

	useTables(): n8n.Table[] {
		const [tables, setTables] = React.useState<n8n.Table[]>([]);

		React.useEffect(() => {
			this.fetchJson<{ data: n8n.Table[] }>('/api/v1/data-tables')
				.then(tables => setTables(tables.data.map(i => ({
					...i,
					updatedAt: new Date(i.updatedAt),
					createdAt: new Date(i.createdAt),
				}))));
		}, []);

		return tables;
	}

	useCustomerProjects(id: string = '', filter: n8n.TableFilter = {}): n8n.TableRowWith<n8n.KundenProjektAuftrag>[] {
		const [table, setTable] = React.useState<n8n.TableRowWith<n8n.KundenProjektAuftrag>[]>([]);

		const filterString = React.useMemo(() => new URLSearchParams(Object.entries(filter)), [filter])

		React.useEffect(() => {
			this.fetchJson<n8n.Response<n8n.TableRowWith<n8n.KundenProjektAuftrag>[]>>(`/api/v1/data-tables/${config.n8n.customerTableId}/rows?${filterString}`)
				.then(table => setTable(table.data.map(table => ({
					...table,
					updatedAt: new Date(table.updatedAt),
					createdAt: new Date(table.createdAt),
				}))))
		}, []);

		return table;
	}

	useRecentRuns(): n8n.KickoffRun[] | null {
		const [runs, setRuns] = React.useState<n8n.KickoffRun[] | null>(null);

		React.useEffect(() => {
			this.getRecentRuns()
				.then(runs => setRuns(runs));
		}, []);

		return runs;
	}
}
